// Fee calculation engine.
//
// Single source of truth for turning (client contract terms + candidate
// compensation) into an expected recruitment fee. Pure functions only - no
// React, no DB - so the same logic runs in the FeeCalculator UI and could run
// server-side. Modelled on the six Pearson & Partners "Opdrachtbevestiging"
// contracts and retained agreements:
//
//   1. NCNP                       - 30% of bruto jaarsalaris, min €11.900
//   2. NCNP + Garantie            - same fee + 8-weken terugbetalingsstaffel
//   3. NCNP Fixed Fee             - vast bedrag per ervaringsniveau
//   4. NCNP Fixed Fee + Garantie  - idem + staffel
//   5. Retained Partnership       - 30% / min €11.900, retainer (voorinvestering)
//                                   wordt bij 1e plaatsing in mindering gebracht
//   6. Retained Partnership + Garantie - idem + staffel
//   7. Retained Search Fixed Fee  - vaste voorinvestering + vaste succesfee
//
// Everything variable in those contracts is a term on the client (percentage,
// minimum, fixed-fee bedragen, retainer, garantie, btw, betalingstermijn, welke
// emolumenten meetellen). The candidate side supplies the salary + emolumenten.

export const CONTRACT_TYPES = [
  { id: 'ncnp', label: 'No Cure No Pay (% van jaarsalaris)' },
  { id: 'fixed', label: 'Fixed Fee (vast bedrag per niveau)' },
  { id: 'retained', label: 'Retained Partnership (% + voorinvestering)' },
  { id: 'retained-fixed', label: 'Retained Search (vaste voorinvestering + vaste succesfee)' },
  // Detavast: geen fee-berekening; de klant betaalt maandelijks en de omzet wordt
  // handmatig op het proces ingevoerd (owner-besluit). computeFee() geeft hier null.
  { id: 'detavast', label: 'Detavast (maandelijkse facturatie)' },
  // Freelance contracting: de klant betaalt een uurtarief; net als detavast is de
  // omzet handmatig op het proces (computeFee() geeft null). Het uurtarief zelf is
  // wel een term (terms.uurtarief) en bepaalt of de terms "gezet" zijn.
  // Heet sinds 4 september 2026 gewoon Contracting: dat is de naam van het
  // klanttype waar dit contracttype bij hoort.
  { id: 'freelance', label: 'Contracting (uurtarief)' },
];

// Wanneer het honorarium gefactureerd wordt. Twee waarden, want meer staat er in
// geen enkele opdrachtbevestiging. Leeg = niet vastgelegd, nooit een default:
// een afspraak die niemand gemaakt heeft mag de app niet claimen.
export const FACTURATIE_MOMENTEN = [
  { id: 'ondertekening', label: 'Bij ondertekening' },
  { id: 'start', label: 'Bij startdatum' },
];

export const SENIORITY = [
  { id: 'junior', label: 'Junior' },
  { id: 'medior', label: 'Medior' },
  { id: 'senior', label: 'Senior' },
];

// De drie niveau-ids als platte lijst, voor tier-validatie (deal.feeTier e.d.).
export const TIER_IDS = SENIORITY.map((s) => s.id);

// Niveau afleiden uit jaren werkervaring (de kaartwaarde, kaartJarenErvaring in
// lib/qual-card-facts.mjs). 0/leeg levert null, dus nooit 'junior' gokken: in de
// RecruitCRM-spiegel was 0 de standaard voor onbekend, en een starter op de kaart telt
// hier bewust ook niet als junior.
export function seniorityFromYears(years) {
  const y = Number(years);
  if (!(y > 0)) return null;
  if (y < 3) return 'junior';
  if (y < 8) return 'medior';
  return 'senior';
}

// 8-weken garantie-terugbetalingsstaffel (contract 2/4/6). Informational -
// shown when guarantee is on; it does not change the base fee, only the refund
// risk if the placement falls through.
export const GUARANTEE_STAFFEL = [
  { weeks: 'Week 1 t/m 4', pct: 50 },
  { weeks: 'Week 5 t/m 6', pct: 25 },
  { weeks: 'Week 7 t/m 8', pct: 12.5 },
];

// Signed contracts can contain a client-specific guarantee schedule. Keep the
// standard 8-week schedule as the fallback, but never show it for a contract
// that explicitly stores a different schedule.
export function guaranteeScheduleFor(terms) {
  const custom = Array.isArray(terms?.guaranteeSchedule)
    ? terms.guaranteeSchedule.filter((row) => String(row?.weeks || '').trim() && Number.isFinite(Number(row?.pct)))
    : [];
  return custom.length
    ? custom.map((row) => ({ weeks: String(row.weeks).trim(), pct: Number(row.pct) }))
    : GUARANTEE_STAFFEL;
}

// Defaults straight from the standard contracts. Per client these are editable.
// The actual fee AMOUNTS (feePct, fixedFee, uurtarief) start blank: a client
// shows no fee until an owner or director explicitly sets and saves one.
// fixedFee is a single fallback amount; per-niveau bedragen leven optioneel in
// fixedFeeTiers ({junior,medior,senior}) of fixedFeeSchedule ([{label,amount}]).
export const DEFAULT_FEE_TERMS = {
  contractType: 'ncnp',
  feePct: null,
  minFee: 11900,
  fixedFee: null,
  uurtarief: null,
  retainer: 7500,
  guarantee: false,
  vakantiegeldPct: 8,
  btwPct: 21,
  betalingstermijn: 14,
  // Contractafspraken die WEL in de opdrachtbevestiging staan maar niet in de
  // formule meerekenen. Alle vier bewust neutraal leeg: normalizeFeeTerms vult
  // defaults aan, en een default mag hier nooit een afspraak suggereren die er
  // niet is (export en samenvatting blijven blanco zolang feeTermsSet() false is).
  facturatieMoment: '',        // '' | 'ondertekening' | 'start' (FACTURATIE_MOMENTEN)
  emolumentenAfgesproken: '',  // '' | 'ja' | 'nee' - los van de emolumenten-toggles hieronder
  emolumentenInhoud: '',       // vrije tekst: welke emolumenten precies
  garantieInhoud: '',          // vrije tekst: de garantiestaffel zoals afgesproken
  // Which emolumenten count toward the bruto jaarsalaris for this client.
  emolumenten: {
    dertiende: true,   // 13e maand (+ 1 maandsalaris)
    bonus: true,       // 100% van max. provisie/bonus (€/jaar)
    onkosten: false,   // reis-/onkostenvergoeding (€/jaar)
    lease: true,       // leasebijdrage (€/maand → ×12)
    pensioen: false,   // werkgeversbijdrage pensioen (€/jaar)
    tantieme: true,    // tantième (€/jaar)
  },
};

// Back-compat: fixedFee used to be { junior, medior, senior }. Collapse any old
// tiered value to a single amount (prefer medior, the historical default), and
// pass a plain number/blank through unchanged.
function singleFixedFee(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object') {
    const pick = v.medior ?? v.senior ?? v.junior ?? null;
    return pick === '' || pick == null ? null : Number(pick);
  }
  return Number(v);
}

// Merge stored (possibly partial) terms over the defaults so older clients with
// only a few fields still compute.
export function normalizeFeeTerms(stored) {
  const s = stored || {};
  return {
    ...DEFAULT_FEE_TERMS,
    ...s,
    fixedFee: singleFixedFee(s.fixedFee),
    emolumenten: { ...DEFAULT_FEE_TERMS.emolumenten, ...(s.emolumenten || {}) },
  };
}

export function isFixedFeeType(type) {
  return type === 'fixed' || type === 'retained-fixed';
}

export function isRetainedFeeType(type) {
  return type === 'retained' || type === 'retained-fixed';
}

// Detavast en contracting (freelance): geen rekenbare fee. De omzet zet de consultant
// handmatig op het proces (deal.expectedFee); computeFee geeft null. Zo'n afspraak IS
// gezet (feeTermsSet), ook zolang die fee nog 0 is: de kaart (missingForDeal in
// lib/dealForecast.js) en het raster (PROC_MISSING) noemen de afspraak dan niet
// ontbrekend (eigenaarsbesluit 26 september 2026).
export function isManualFeeType(type) {
  return type === 'detavast' || type === 'freelance';
}

// Een staffel zonder los bedrag is ook een afspraak. SQL-tweeling is _feeSet in
// lib/search/catalogs-extra.mjs, die twee veranderen samen.
export function feeTermsSet(fee) {
  if (!fee) return false;
  if (fee.contractType === 'detavast') return true;
  if (fee.contractType === 'freelance') return Number(fee.uurtarief) > 0;
  if (isFixedFeeType(fee.contractType)) return Number(fee.fixedFee) > 0 || fixedFeeScheduleRows(fee).length > 0;
  return Number(fee.feePct) > 0;
}

// ── Welke honorariumafspraak geldt (een feit, een bron, 25 september 2026) ──
// De afspraak van een vacature of plaatsing is EEN keuze: de vacature-eigen
// afspraak (job_terms, de snapshot van een gekozen versie) wint zodra hij een
// bedrag draagt, anders de standaard van de klant (client_terms.terms.fee), ook
// alleen met een bedrag, anders geldt er geen. 'Een bedrag dragen' is feeTermsSet
// hierboven. Tot deze datum koos /api/fee op een gevulde blob, de forecast op
// feeTermsSet en lazen het dealformulier en de CV-send-briefing alleen de klant:
// bij Myst (deal 1779110439045) rekende de Fee calculator met de job-terms
// (14.000, 14 dagen) terwijl het dealformulier de klantstandaard voorvulde
// (staffel vanaf 12.000, 30 dagen). Kies hier, nergens anders; een enkele
// vacature of klant laadt via loadFeeTerms (lib/contract-terms.mjs), een batch
// leest de rijen zelf en geeft ze hier door. De ratel staat in
// lib/one-fact-one-source.test.mjs.
//   jobTerms     de job_terms-rij { fee, name, version_id }, of null
//   clientTerms  de standaard-fee van de klant (client_terms.terms.fee), of null
// -> { fee, source: 'job' | 'client' | null, name, versionId }
export function resolveFeeTerms({ jobTerms = null, clientTerms = null } = {}) {
  if (jobTerms && feeTermsSet(jobTerms.fee)) {
    return {
      fee: jobTerms.fee,
      source: 'job',
      name: jobTerms.name || null,
      versionId: jobTerms.version_id ?? jobTerms.versionId ?? null,
    };
  }
  if (feeTermsSet(clientTerms)) return { fee: clientTerms, source: 'client', name: null, versionId: null };
  return { fee: null, source: null, name: null, versionId: null };
}

// De herkomst als label, zoals de hover bij een fee hem toont: de naam van de
// gekozen versie, anders 'Job-terms'; de klantstandaard heet 'Klantstandaard'.
export function feeTermsLabel(resolved) {
  if (resolved?.source === 'job') return resolved.name || 'Job-terms';
  if (resolved?.source === 'client') return 'Klantstandaard';
  return null;
}

// Vast bedrag voor een niveau (tier: 'junior'|'medior'|'senior') op fixed-fee
// terms. Volgorde: (1) de zichtbare staffel (fixedFeeSchedule, de enige vorm
// die ClientTerms bewerkt), (2) alleen als die geen bedragen heeft het oude
// fixedFeeTiers-object als Junior/Medior/Senior-rijen, (3) 0 = geen match,
// caller valt terug op terms.fixedFee. Zelfde lezer als feeSummary en
// fixedFeeScheduleLine, dus berekening en weergave kunnen niet uiteenlopen.
function tierFixedFee(terms, tier) {
  if (!tier || !TIER_IDS.includes(tier)) return 0;
  const row = fixedFeeScheduleRows(terms).find((r) => r.label.toLowerCase().startsWith(tier));
  return row ? row.amount : 0;
}

// De staffelrijen van fixed-fee terms als één genormaliseerde lijst
// [{label, amount}], alleen rijen met een echt bedrag. Bron 1 is de vrije
// fixedFeeSchedule; ontbreekt die, dan het oudere fixedFeeTiers-object
// ({junior,medior,senior}) als Junior/Medior/Senior-rijen. Leeg -> [].
export function fixedFeeScheduleRows(terms) {
  const t = terms || {};
  const rows = (Array.isArray(t.fixedFeeSchedule) ? t.fixedFeeSchedule : [])
    .map((r) => ({ label: String((r && r.label) ?? '').trim(), amount: Number(r && r.amount) }))
    .filter((r) => r.amount > 0);
  if (rows.length) return rows;
  const tiers = t.fixedFeeTiers;
  if (!tiers || typeof tiers !== 'object') return [];
  return SENIORITY
    .map((s) => ({ label: s.label, amount: Number(tiers[s.id]) }))
    .filter((r) => r.amount > 0);
}

// Eénregelige weergave van de fixed-fee staffel. Tot en met vier niveaus staan de
// bedragen er voluit ('Junior € 11.500, Medior+ € 14.000'); daarboven wordt het
// een bereik, anders loopt de regel de kolom uit. Geen staffel -> null, zodat de
// caller op het enkele fixedFee-bedrag terugvalt.
export function fixedFeeScheduleLine(terms) {
  const rows = fixedFeeScheduleRows(terms);
  if (!rows.length) return null;
  const eur = (n) => `€ ${Number(n).toLocaleString('nl-NL')}`;
  if (rows.length <= 4) return rows.map((r) => (r.label ? `${r.label} ${eur(r.amount)}` : eur(r.amount))).join(', ');
  const amounts = rows.map((r) => r.amount);
  const lo = Math.min(...amounts);
  const hi = Math.max(...amounts);
  return lo === hi ? `${eur(lo)} per niveau` : `${eur(lo)} tot ${eur(hi)} per niveau`;
}

// Lenient euro/number parse. Accepts "€ 5.500", "5500", "5.500,50", "4250,-",
// numbers. Returns a finite number or 0. NL convention: '.' = thousands,
// ',' = decimals.
export function parseEuro(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;
  let s = String(v).trim();
  if (!s) return 0;
  s = s.replace(/[^0-9.,-]/g, '');           // drop €, spaces, "p/m", etc.
  s = s.replace(/[.,]-$/, '');               // trailing ",-" / ".-"
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.'); // 1.234,56 → 1234.56
  } else if ((s.match(/\./g) || []).length > 1) {
    s = s.replace(/\./g, '');                // 1.234.567 → 1234567
  } else if (/\.\d{3}$/.test(s)) {
    s = s.replace(/\./g, '');                // 5.500 → 5500 (thousands, not decimal)
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

// DE canonieke euroweergave van de app (eigenaarsbesluit 13 aug 2026): euroteken,
// een spatie, punt als duizendtalscheiding, komma als decimaalteken, ALTIJD twee
// decimalen. Elk bedrag in de UI loopt hierlangs, definieer nergens anders een
// eigen euro/fmtEuro.
//
// De spatie is een harde spatie (U+00A0), zo stond het er al. Dat is opzet: in een
// mailclient mag het bedrag niet halverwege afbreken, het euroteken blijft aan het
// getal vastzitten. Visueel niet te onderscheiden van een gewone spatie.
//
// Opties, alleen voor zinnen die het teken of de centen zelf al regelen:
//   { symbol: false } laat het euroteken weg
//   { cents: false }  laat de decimalen weg
// JobMailModal schrijft bijvoorbeeld "6.500 euro bruto per maand" in mailtekst en
// gebruikt daarom { symbol: false, cents: false }.
// Een boolean als tweede argument houdt de oude withCents-betekenis.
export function fmtEuro(v, opts = {}) {
  const o = typeof opts === 'boolean' ? { cents: opts } : (opts || {});
  const cents = o.cents !== false;
  const n = Number(v) || 0;
  const num = n.toLocaleString('nl-NL', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
  return o.symbol === false ? num : '€ ' + num;
}

// Pull a candidate-compensation seed from a kandidaat-kwalificatie `data` blob.
//
// Het SALARIS zit er sinds 15 september 2026 NIET meer in: de kwalificatie bewaart
// geen bedrag meer, candidate_overrides is de enige bron (lib/salary.mjs). Deze
// functie is puur en wordt ook client-side aangeroepen (dealAdapter), dus ze kan
// niet zelf resolven; de aanroeper geeft het al opgeloste maandbedrag mee als
// tweede argument (huidig, anders de ondergrens - dezelfde volgorde als vroeger).
// De emolumenten (vakantiegeld, 13e, bonus, onkosten, lease, pensioen) blijven wel
// kwalificatievelden: die horen bij het AANBOD, niet bij het kandidaat-salaris.
export function compFromQualification(data, monthlyPm = null) {
  const d = data || {};
  return {
    monthly: Number(monthlyPm) || 0,
    vakantiegeldPct: parseEuro(d.vakantiegeld) || null,
    dertiende: /ja/i.test(String(d.dertiendeMaand || '')),
    bonusEuro: parseEuro(d.bonusEuro),
    onkostenEuro: parseEuro(d.onkostenvergoeding),
    leaseMaand: parseEuro(d.leaseauto),
    pensioenEuro: parseEuro(d.pensioen),
    tantiemeEuro: 0,
  };
}

// Build the bruto jaarsalaris and its line-item breakdown.
//   inputs.monthly       - bruto maandsalaris o.b.v. 40u (€)
//   inputs.emolumenten   - { dertiende, bonus, onkosten, lease, pensioen, tantieme }
//                          numeric amounts (annual €, except lease which is €/maand)
//   terms                - normalized fee terms (vakantiegeldPct, emolumenten toggles)
export function computeJaarsalaris(inputs, terms) {
  const t = normalizeFeeTerms(terms);
  const monthly = Number(inputs.monthly) || 0;
  const em = inputs.emolumenten || {};
  const vakPct = inputs.vakantiegeldPct != null ? Number(inputs.vakantiegeldPct) : t.vakantiegeldPct;

  const base = monthly * 12;
  const vakantiegeld = base * (vakPct / 100);

  const lines = [
    { key: 'base', label: 'Bruto maandsalaris × 12', amount: base, always: true },
    { key: 'vakantiegeld', label: `Vakantiegeld (${vakPct}%)`, amount: vakantiegeld, always: true },
  ];

  const add = (key, label, amount) => {
    const on = t.emolumenten[key];
    const amt = Number(amount) || 0;
    lines.push({ key, label, amount: on ? amt : 0, included: on, value: amt });
  };
  add('dertiende', '13e maand', em.dertiende != null ? em.dertiende : monthly);
  add('bonus', 'Provisie / bonus (100% max, €/jaar)', em.bonus);
  add('onkosten', 'Reis-/onkostenvergoeding (€/jaar)', em.onkosten);
  add('lease', 'Leasebijdrage (€/jaar)', em.lease);
  add('pensioen', 'Werkgeversbijdrage pensioen (€/jaar)', em.pensioen);
  add('tantieme', 'Tantième (€/jaar)', em.tantieme);

  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  return { base, vakantiegeld, vakPct, lines, total };
}

// Main entry. Returns a full, display-ready breakdown.
//   args.terms       - stored (or normalized) client fee terms
//   args.monthly     - bruto maandsalaris o.b.v. 40u (€)
//   args.tier        - optioneel niveau ('junior'|'medior'|'senior') voor
//                      fixed-fee terms met een fixedFeeSchedule of, als die geen
//                      bedragen heeft, het oudere fixedFeeTiers; zonder tier of
//                      zonder match geldt terms.fixedFee als vanouds
//   args.emolumenten - numeric emolument amounts (see computeJaarsalaris)
//   args.vakantiegeldPct - optional override
export function computeFee(args) {
  const terms = normalizeFeeTerms(args.terms);
  const type = terms.contractType;
  // Detavast + freelance: geen berekening. De omzet is handmatig (deal.expectedFee),
  // dus er valt hier niets te rekenen. Callers behandelen null als "handmatige omzet".
  if (isManualFeeType(type)) return null;
  const jaar = computeJaarsalaris(
    { monthly: args.monthly, emolumenten: args.emolumenten, vakantiegeldPct: args.vakantiegeldPct },
    terms,
  );
  const jaarsalaris = jaar.total;

  let feeExBtw = 0;
  let feeRaw = 0;          // pct × jaarsalaris, before the minimum
  let minApplied = false;
  let retainerPrepaid = 0;
  let remainingAtPlacement = null;
  let unset = false;       // no fee amount agreed yet -> caller shows blank, not € 0

  if (isFixedFeeType(type)) {
    // Vast bedrag. Met een tier-argument wint het niveaubedrag (eerst een
    // schedule-rij met passend label, anders het oudere fixedFeeTiers);
    // anders het enkele fixedFee-bedrag. Blank until an owner/director sets it.
    const tiered = tierFixedFee(terms, args.tier);
    if (tiered > 0) {
      feeExBtw = tiered;
      feeRaw = feeExBtw;
      if (type === 'retained-fixed') {
        retainerPrepaid = Number(terms.retainer) || 0;
        remainingAtPlacement = feeExBtw;
      }
    } else if (terms.fixedFee == null || terms.fixedFee === '') {
      unset = true;
    } else {
      feeExBtw = Number(terms.fixedFee) || 0;
      feeRaw = feeExBtw;
      if (type === 'retained-fixed') {
        retainerPrepaid = Number(terms.retainer) || 0;
        remainingAtPlacement = feeExBtw;
      }
    }
  } else if (terms.feePct == null || terms.feePct === '') {
    // ncnp + retained: blank until a percentage has been set.
    unset = true;
  } else {
    // percentage of jaarsalaris, floored at the minimum.
    feeRaw = jaarsalaris * (terms.feePct / 100);
    feeExBtw = Math.max(feeRaw, terms.minFee || 0);
    minApplied = feeExBtw > feeRaw + 0.001;
    if (type === 'retained') {
      retainerPrepaid = Number(terms.retainer) || 0;
      remainingAtPlacement = Math.max(0, feeExBtw - retainerPrepaid);
    }
  }

  const btw = feeExBtw * (terms.btwPct / 100);
  const feeInclBtw = feeExBtw + btw;

  return {
    type,
    typeLabel: (CONTRACT_TYPES.find((c) => c.id === type) || {}).label || type,
    unset,
    guarantee: !!terms.guarantee,
    guaranteeSchedule: guaranteeScheduleFor(terms),
    jaarsalaris,
    jaarsalarisBreakdown: jaar.lines,
    feePct: terms.feePct,
    feeRaw,
    minFee: terms.minFee,
    minApplied,
    feeExBtw,
    btwPct: terms.btwPct,
    btw,
    feeInclBtw,
    retainerPrepaid,
    remainingAtPlacement,
    totalContractValue: type === 'retained-fixed' ? retainerPrepaid + feeExBtw : feeExBtw,
    betalingstermijn: terms.betalingstermijn,
  };
}

// ── Bedragen: de fee en het salaris (eigenaarsregel 13 september 2026) ────────
// Regel 33 van DESIGN-SYSTEM.md: een bedrag op een kaart draagt de muntglyph, dus
// staat het euroteken NIET in de tekst. Deze helpers zijn de enige vorm daarvan;
// `fmtEuro` blijft de canonieke weergave voor omzet, targets en ranglijsten.
//
// Fee:     '12.000,00'  (centen, want een honorarium wordt zo gefactureerd)
// Salaris: '5.500 bpm'  (bruto per maand, geen centen)

export function fmtFee(v) {
  return fmtEuro(v, { symbol: false });
}

// Een maandsalaris boven deze drempel bestaat in deze markt niet: zo'n getal is
// een jaarbedrag uit de import (RecruitCRM mengt jaar en maand in dezelfde
// kolom). Dat krijgt 'per jaar' achter zich in plaats van 'bpm', zonder
// omrekening: de bron is niet genormaliseerd en delen door twaalf zou een
// nauwkeurigheid claimen die er niet is.
export const SALARIS_JAAR_DREMPEL = 25000;
export function isJaarbedrag(v) { return (Number(v) || 0) > SALARIS_JAAR_DREMPEL; }
export function fmtSalaris(v) {
  return fmtEuro(v, { symbol: false, cents: false }) + (isJaarbedrag(v) ? ' per jaar' : ' bpm');
}

// Een salarisband op een kaart. Koppelteken met spaties ertussen, nooit een
// gedachtestreepje (scheidingstekenregel). Een open kant wordt een woord.
export function fmtSalarisRange(lo, hi) {
  const a = Number(lo) || 0;
  const b = Number(hi) || 0;
  if (a > 0 && b > 0) return `${fmtEuro(a, { symbol: false, cents: false })} - ${fmtSalaris(b)}`;
  if (a > 0) return `vanaf ${fmtSalaris(a)}`;
  if (b > 0) return `tot ${fmtSalaris(b)}`;
  return null;
}

// Een band mag als [lo, hi], {lo, hi} of {min, max} binnenkomen: de aanroepers
// komen uit job_salary (min/max) en uit de kandidaatresolver (lo/hi).
export function salarisRangeOf(range) {
  if (!range) return null;
  const pair = Array.isArray(range)
    ? { lo: range[0], hi: range[1] }
    : { lo: range.lo ?? range.min, hi: range.hi ?? range.max };
  const lo = Number(pair.lo) || 0;
  const hi = Number(pair.hi) || 0;
  return (lo > 0 || hi > 0) ? { lo, hi } : null;
}

// De formule achter een fee, afgeleid uit een computeFee()-uitkomst plus de
// herkomst van de voorwaarden. Dit object reist mee naar de kaart, zodat de
// hover de berekening kan tonen zonder hem over te doen. Detavast en freelance
// hebben geen formule: computeFee geeft daar null, dus deze functie ook.
export function feeFormula(result, extra = {}) {
  if (!result) return null;
  const base = (result.jaarsalarisBreakdown || []).find((l) => l.key === 'base');
  const monthly = base ? Math.round((Number(base.amount) || 0) / 12) : 0;
  const fixed = isFixedFeeType(result.type);
  return {
    contractType: result.type,
    termsSource: extra.termsSource || null,
    tier: fixed ? (extra.tier || null) : null,
    monthly,
    jaarsalaris: result.jaarsalaris,
    pct: (result.feePct == null || result.feePct === '') ? null : Number(result.feePct),
    feeRaw: result.feeRaw,
    minFee: result.minFee,
    minApplied: !!result.minApplied,
    fixedFee: fixed ? result.feeExBtw : null,
    feeExBtw: result.feeExBtw,
  };
}

// De hover bij een fee: titel, korte regels, en de berekening als raster. Vorm
// van <HintBody> (components/HomeV2/lib.js), dus regels blijven kort.
//   source: 'vast' (vastgelegd op het proces) | 'geschat' (berekend) |
//           'handmatig' (detavast en freelance) | null
export function feeHint(source, value, formula) {
  const title = 'Fee';
  if (source === 'handmatig') {
    return {
      title,
      lines: ['Omzet wordt handmatig op het proces gezet'],
      rows: value ? [['Fee', fmtEuro(value)]] : [],
    };
  }
  const f = formula || null;
  if (!f) {
    let lines = [];
    if (source === 'geschat') lines = ['Geschat uit honorarium en salaris, nog niet vastgelegd'];
    else if (source === 'vast') lines = ['Vastgelegd op het proces'];
    return { title, lines, rows: value ? [['Fee excl. btw', fmtEuro(value)]] : [] };
  }
  const lines = [{ label: 'Voorwaarden', text: f.termsSource || 'niet vastgelegd' }];
  if (isFixedFeeType(f.contractType)) {
    return {
      title,
      lines,
      rows: [
        [f.tier ? `Vast bedrag, ${f.tier}` : 'Vast bedrag', fmtEuro(f.fixedFee)],
        ['Fee excl. btw', fmtEuro(f.feeExBtw)],
      ],
    };
  }
  const rows = [
    ['Bruto maandsalaris', fmtEuro(f.monthly, { cents: false }) + ' bpm'],
    ['Bruto jaarsalaris', fmtEuro(f.jaarsalaris)],
    [`${f.pct}% van jaarsalaris`, fmtEuro(f.feeRaw)],
  ];
  if (f.minApplied) rows.push(['Minimum honorarium', fmtEuro(f.minFee)]);
  rows.push(['Fee excl. btw', fmtEuro(f.feeExBtw)]);
  return { title, lines, rows };
}

// De hover bij een salaris. Een salarisbedrag in de app betekent ALTIJD hetzelfde
// (bruto per maand, 40 uur, zonder emolumenten) en die zin staat daarom op een
// plek, niet in elke kaart opnieuw.
export const SALARIS_HINT_PREFIX = 'Bruto per maand op basis van 40 uur, zonder emolumenten';

export function salarisHint(value, range) {
  const eur = (v) => fmtEuro(v, { cents: false });
  const r = salarisRangeOf(range);
  let text;
  if (r && r.lo > 0 && r.hi > 0) text = `${eur(r.lo)} tot ${eur(r.hi)}`;
  else if (r && r.lo > 0) text = `vanaf ${eur(r.lo)}`;
  else if (r && r.hi > 0) text = `tot ${eur(r.hi)}`;
  else if (isJaarbedrag(value)) return { title: 'Salaris', lines: [`Jaarbedrag uit de import, niet omgerekend naar per maand: ${eur(value)}`] };
  else text = eur(value);
  return { title: 'Salaris', lines: [`${SALARIS_HINT_PREFIX}: ${text}`] };
}
